import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/** Form controls. Every control is labelled; hints and errors are wired up with aria. */

type FieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
  /** Put the label after the control (checkbox / switch). */
  inline?: boolean;
  className?: string;
};

export function Field({ label, hint, error, children, inline, className }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={['field', inline ? 'field-inline' : '', className || ''].filter(Boolean).join(' ')}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p className="field-hint subtle" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={['control', invalid ? 'control-invalid' : '', className || ''].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={['control', 'control-area', invalid ? 'control-invalid' : '', className || ''].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ className, invalid, children, ...rest }, ref) {
    return (
      <div className="select-wrap">
        <select
          ref={ref}
          className={['control', 'control-select', invalid ? 'control-invalid' : '', className || ''].filter(Boolean).join(' ')}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {children}
        </select>
      </div>
    );
  },
);

export function Checkbox({
  label,
  hint,
  ...rest
}: { label: ReactNode; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="checkbox">
      <input type="checkbox" {...rest} />
      <span className="checkbox-body">
        <span>{label}</span>
        {hint ? <span className="subtle checkbox-hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export function Switch({
  label,
  checked,
  onChange,
  disabled,
  describedBy,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** The id of the text that says why the switch is off or unavailable, so a screen reader reads the reason with it. */
  describedBy?: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch-track" aria-hidden>
        <span className="switch-thumb" />
      </span>
      <span>{label}</span>
    </label>
  );
}

export type SegmentOption<T extends string> = { value: T; label: ReactNode; icon?: ReactNode };

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
  disabled,
}: {
  options: Array<SegmentOption<T>>;
  value: T;
  onChange: (next: T) => void;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  /** Read-only surfaces need this as much as any input does: the vendor
   * portal shows a submitted response with its compliance control still on
   * screen, and a click that the server will refuse must not be offered. */
  disabled?: boolean;
}) {
  return (
    <div className={`segmented segmented-${size}`} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'is-active' : ''}
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The rating used on every scorecard cell: 1 to `max` (the project's scale).
 *
 * `max` defaults to 5 and is deliberately optional. The Contributors screen's
 * vote weight is ALSO a rating, 1–5, and it is a weight, not a score: it must
 * not follow a project's scale. Only a caller that is rating a vendor passes
 * the project's maximum. (Section and criterion weights are not ratings: they
 * are percentages, typed — see WeightPercentInput.)
 */
export function Rating({
  value,
  onChange,
  disabled,
  label,
  size = 'md',
  max = 5,
  min = 1,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  max?: number;
  /** The low end of the card. Below 1 only when the owner set a range that starts at 0. */
  min?: number;
}) {
  return (
    <div className={`rating rating-${size}`} role="group" aria-label={label}>
      {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => {
        // `value != null`, not Boolean(value): on a 0-based card a 0 is a real
        // mark and must light its button, where an empty cell is null.
        const on = value != null && value >= n;
        return (
          <button
            key={n}
            type="button"
            className={on ? 'is-on' : ''}
            disabled={disabled}
            aria-pressed={on}
            aria-label={`${n}`}
            onClick={() => onChange(value === n ? null : n)}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The score a pressed key stands for on a min..max card, or null if it is not
 * one. A digit is itself when the card has it. On a card with no 0 and ten or
 * more steps there is no key for "10", so 0 is 10 (the same way a keyboard's
 * number row reads); on a card that starts at 0, 0 is a 0 and the top mark of
 * a 0-10 card is clicked, not keyed — one key cannot mean both. A key outside
 * the card is not a score: pressing 8 on a 1–5 card does nothing.
 */
export function ratingFromKey(key: string, max: number, min = 1): number | null {
  if (key.length !== 1 || key < '0' || key > '9') return null;
  const digit = Number(key);
  if (digit >= min && digit <= max) return digit;
  if (digit === 0 && min > 0 && max === 10) return 10;
  return null;
}

/** The keys that rate on a min..max card, as the footer of a scorecard shows them. */
export function RatingKeys({ max, min = 1 }: { max: number; min?: number }) {
  if (max >= 10 && min > 0) {
    return (
      <>
        <kbd>{min}</kbd>–<kbd>9</kbd>, <kbd>0</kbd> = 10
      </>
    );
  }
  const top = Math.min(max, 9);
  return (
    <>
      <kbd>{min}</kbd>–<kbd>{top}</kbd>
    </>
  );
}
